import { useState, useCallback, useEffect } from 'react';
import { ITripGroup } from '../types/trip.types';
import { findFirstPendingTripIndex, findNextPendingTrip } from '../services/trip.service';
import { areAllTripsResolved } from '../utils/trip-navigation.utils';

export const useTripNavigation = (tripGroups: ITripGroup[]) => {
    const [selectedTripIndex, setSelectedTripIndex] = useState<number>(0);
    const [viewType, setViewType] = useState<'trip' | 'overall'>('trip');
    const [lastOpenedTripId, setLastOpenedTripId] = useState<number | null>(null);

    useEffect(() => {
        if (selectedTripIndex >= tripGroups.length && tripGroups.length > 0) {
            setSelectedTripIndex(0);
        }
    }, [tripGroups.length, selectedTripIndex]);

    const selectTrip = useCallback((index: number) => {
        setSelectedTripIndex(index);
        setViewType('trip');
    }, []);

    const selectNextPendingTrip = useCallback((currentIndex: number) => {
        if (tripGroups.length === 0) return null;
        
        if (areAllTripsResolved(tripGroups)) {
            setViewType('overall');
            setSelectedTripIndex(0);
            return null;
        }
        
        const nextIndex = findNextPendingTrip(tripGroups, currentIndex);
        if (nextIndex !== null) {
            setSelectedTripIndex(nextIndex);
            setViewType('trip');
            return nextIndex;
        } else {
            const firstPendingIndex = findFirstPendingTripIndex(tripGroups);
            if (firstPendingIndex !== null) {
                setSelectedTripIndex(firstPendingIndex);
                setViewType('trip');
                return firstPendingIndex;
            }
        }
        return null;
    }, [tripGroups]);

    const wrappedSetViewType = useCallback((type: 'trip' | 'overall') => {
        setViewType(type);
    }, [viewType, selectedTripIndex]);

    const wrappedSetSelectedTripIndex = useCallback((index: number) => {
        setSelectedTripIndex(index);
    }, [selectedTripIndex, viewType, tripGroups.length]);

    return {
        selectedTripIndex,
        viewType,
        lastOpenedTripId,
        selectTrip,
        selectNextPendingTrip,
        setViewType: wrappedSetViewType,
        setSelectedTripIndex: wrappedSetSelectedTripIndex,
        setLastOpenedTripId
    };
};

